import { Stack } from '@mui/material'
import { isNotNullOrUndefined, isKnownFuelType, joinDefinedStrings, tokens } from 'ComponentLibrary'
import { LightningIcon } from '@phosphor-icons/react'
import { Typography } from 'ComponentLibrary/src/piazza'
import InfoValueWithUnit from 'ComponentLibrary/src/widgets/AssetInfoBoxes/InfoValueWithUnit'
import { AssetEnergySummary } from './types'
import { isEmpty, uniq } from 'lodash'
import { CSSProperties } from 'react'
import { KnownFuelTypes } from 'ComponentLibrary/src/common/fuel-type'

export type EnergyInfoBoxCopy = {
    condensedTitle: string
    spaciousSectionLabel: string
    spaciousTitle: string
    energyEfficiencyClassLabel: string
    heatingTypeLabel: string
    primaryPowerConsumptionLabel: string
    primaryPowerConsumptionUnit: string
    carbonDioxideEquivalentLabel: string
    carbonDioxideEquivalentUnit: string
    fuelTypeLabels: Record<KnownFuelTypes, string>
}

type EnergyInfoBoxProps = {
    assetEnergySummary?: AssetEnergySummary
    copy: EnergyInfoBoxCopy
    isAssetEnergySummaryPending?: boolean
    backgroundColor?: CSSProperties['backgroundColor']
    variant?: 'condensed' | 'spacious'
}

export function EnergyInfoBox({
    assetEnergySummary,
    copy,
    isAssetEnergySummaryPending,
    backgroundColor,
    variant = 'condensed',
}: EnergyInfoBoxProps) {
    const Component = variant === 'condensed' ? CondensedEnergyInfo : SpaciousEnergyInfo
    return (
        <Stack
            direction="column"
            padding={tokens.SizingSpacing200}
            gap={tokens.SizingSpacing150}
            border={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
            borderRadius={tokens.SizingCornerL}
            sx={{ backgroundColor }}
        >
            <Component assetEnergySummary={assetEnergySummary} copy={copy} isAssetEnergySummaryPending={isAssetEnergySummaryPending} />
        </Stack>
    )
}

function CondensedEnergyInfo({
    assetEnergySummary,
    copy,
    isAssetEnergySummaryPending,
}: Pick<EnergyInfoBoxProps, 'assetEnergySummary' | 'copy' | 'isAssetEnergySummaryPending'>) {
    const energy_efficiency_classes = !isEmpty(assetEnergySummary?.energy_efficiency_classes)
        ? assetEnergySummary?.energy_efficiency_classes.join(', ').replace('a_plus', 'A+').toUpperCase()
        : undefined

    return (
        <>
            <LightningIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
            <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                {copy.condensedTitle}
            </Typography>

            <Stack direction="column" gap={tokens.SizingSpacing200}>
                <InfoValueWithUnit
                    value={assetEnergySummary?.primary_power_consumption}
                    unit={copy.primaryPowerConsumptionUnit}
                    label={copy.primaryPowerConsumptionLabel}
                    isPending={isAssetEnergySummaryPending}
                />
                <InfoValueWithUnit
                    value={assetEnergySummary?.total_carbon_dioxide_equivalent}
                    unit={copy.carbonDioxideEquivalentUnit}
                    label={copy.carbonDioxideEquivalentLabel}
                    isPending={isAssetEnergySummaryPending}
                />
                <InfoValueWithUnit
                    value={energy_efficiency_classes}
                    label={copy.energyEfficiencyClassLabel}
                    isPending={isAssetEnergySummaryPending}
                />
            </Stack>
        </>
    )
}

function SpaciousEnergyInfo({
    assetEnergySummary,
    copy,
    isAssetEnergySummaryPending,
}: Pick<EnergyInfoBoxProps, 'assetEnergySummary' | 'copy' | 'isAssetEnergySummaryPending'>) {
    const filteredEnergyEfficiencyClasses =
        assetEnergySummary?.energy_efficiency_classes.filter((energyEfficiencyClass) => energyEfficiencyClass !== 'unknown') || []
    const energy_efficiency_classes = !isEmpty(filteredEnergyEfficiencyClasses)
        ? filteredEnergyEfficiencyClasses.join(', ').replace('a_plus', 'A+').toUpperCase()
        : undefined
    const fuel_types = uniq(assetEnergySummary?.fuel_types)
        ?.filter((fuelType) => isKnownFuelType(fuelType) && isNotNullOrUndefined(fuelType))
        .map((fuelType) => copy.fuelTypeLabels[fuelType])

    const allFuelTypes = joinDefinedStrings(fuel_types, ', ')

    return (
        <>
            <LightningIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
            <Stack direction="column">
                <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                    {copy.spaciousSectionLabel}
                </Typography>
                <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                    {copy.spaciousTitle}
                </Typography>
            </Stack>

            <Stack direction="column" gap={tokens.SizingSpacing050}>
                <InfoValueWithUnit
                    value={energy_efficiency_classes}
                    label={copy.energyEfficiencyClassLabel}
                    isPending={isAssetEnergySummaryPending}
                    variant="spacious"
                />
                <InfoValueWithUnit
                    value={allFuelTypes}
                    label={copy.heatingTypeLabel}
                    isPending={isAssetEnergySummaryPending}
                    variant="spacious"
                />
                <InfoValueWithUnit
                    value={assetEnergySummary?.primary_power_consumption}
                    unit={copy.primaryPowerConsumptionUnit}
                    label={copy.primaryPowerConsumptionLabel}
                    isPending={isAssetEnergySummaryPending}
                    variant="spacious"
                />
                <InfoValueWithUnit
                    value={assetEnergySummary?.total_carbon_dioxide_equivalent}
                    unit={copy.carbonDioxideEquivalentUnit}
                    label={copy.carbonDioxideEquivalentLabel}
                    isPending={isAssetEnergySummaryPending}
                    variant="spacious"
                />
            </Stack>
        </>
    )
}
